import moment from 'moment';
import _ from 'lodash';
import SyncableModel from './SyncableModel';
import { getRootStore } from "../utils/root-store"
import { translate } from "../i18n"
import { Platform } from "react-native"
import config from "../utils/config"
import { Sentry } from "../components"

export default class File extends SyncableModel {
    static name = 'File'

    _fillable = [
      'id', 'fileable_id', 'fileable_type',
      'title', 'type', 'size', 'path',
      'created_by', 'updated_by',
      'created_at', 'updated_at', 'deleted_at', 'synced_at',
    ]

    static endpoint = '/files'

    static allowReplaceUpdate = true

    _appends = [
      'is_can_be_pushed',
      'is_modified',
      'is_not_synced',
      'path_source',
    ]

    /**
     * @param value
     * @returns {string|null}
     */
    getPathAttribute(value = null) {
      const { BASE_URL } = config();
      if (!value) return value;
      if (value.startsWith('data:')) return value;
      if (value.includes('://')) return value.toString().replace(/\/+$/, '');
      if (value.startsWith('/')) return new URL(`/api${value}`, BASE_URL).toString().replace(/\/+$/, '');
      return new URL(value, BASE_URL).toString().replace(/\/+$/, '');
    }

    /**
     * @returns {Object}
     */
    getPathSourceAttribute() {
      const { tokenStore: { token } } = getRootStore();
      return {
        uri: this.__get('path'),
        headers: {
          Authorization: token.access_token && `Bearer ${token.access_token}`
        }
      };
    }

    setPathSourceAttribute(value) {
      return this
    }

    /**
     *
     * @returns {SyncableModel}
     * @private
     */
    storeToServer() {
      const timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');

      const formData = new FormData();
      _.forOwn(_.pick(this.toArray(), this.getFillable()), (value, key) => {
        if (key === 'path' && value) {
          formData.append(key, {
            name: this.title,
            type: this.type,
            uri: this.getAttribute('path')
          });
        } else formData.append(key, value || '');
      });
      const pushModel = () => {
        let promise = Promise.resolve(formData);
        if (Platform.OS === 'web') {
          promise = fetch(this.getAttribute('path'))
            .then(async (response) => {
              formData.set('path', await response.blob(), this.title)
              return formData;
            })
        }
        return promise.then((formData) => this.constructor.createAxios()
          .post(`${this.constructor.endpoint}`, formData, { headers: { "Content-Type": "multipart/form-data;" } }))
      };

      return pushModel()
        .then((r) => (async (response) => {
          const model = this.constructor.reformatResponse((response.data || {}).data || {});
          const context = (await this.constructor.query()
            .withoutGlobalScopes().find(model[this.getKeyName()])) || this;

          if (!_.isEmpty(context.getFillable())) context.fill(_.pick(model, context.getFillable()));
          else {
            _.forEach(model, (value, key) => {
              context[key] = value;
            });
          }
          context.updated_at = model.created_at || model.updated_at || timestamp;
          context.synced_at = model.created_at || model.updated_at || timestamp;
          context.timestamps = false;
          await context.save();
          context.timestamps = true;
          return context;
        })(r))
        .catch((error) => {
          // console.warn(this.constructor.name, error);
          // if (error.response && error.response.status === 422) {
          //   return this.forceDelete().then(() => this);
          // }
          let { message } = error;
          if (error.response) {
            switch (error.response.status) {
              case 403:
                message = (error.response.data || {}).message || error.message;
                break;
              case 422:
                message = Object.keys(error.response.data.errors).map((k) => (
                  `${error.response.data.errors[k].join(', ')}`
                )).join(', ');
                break;
              default:
                message = error.response.message || error.message;
                break;
            }
          }
          if (error && !error.response && error instanceof Error && !error.message.includes('Request Timeout') && !error.message.includes('Network Error')) {
            Sentry.captureException(error);
          }
          if (message === 'Network Error') message = translate(message);
          throw new Error(`[${this.constructor.name}]:\n${message}`);
        });
    }

    /**
     *
     * @returns {SyncableModel|null}
     * @private
     */
    updateToServer() {
      const timestamp = moment().utc().format('YYYY-MM-DD HH:mm:ss');
      const formData = new FormData();
      formData.append('_method', 'PUT');
      _.forOwn(_.pick(this.getDirty(), this.getFillable()), (value, key) => {
        if (key === 'path' && value) {
          formData.append(key, {
            name: this.title,
            type: this.type,
            uri: this.getAttribute('path')
          });
        } else formData.append(key, value || '');
      });
      const pushModel = () => this.constructor.createAxios().post(`${this.constructor.endpoint}/${this.getKey()}`, formData, { headers: { 'Content-Type': 'multipart/form-data;' } });

      return pushModel()
        .then((r) => (async (response) => {
          const model = this.constructor.reformatResponse((response.data || {}).data || {});

          if (!_.isEmpty(this.getFillable())) this.fill(_.pick(model, this.getFillable()));
          else {
            _.forEach(model, (value, key) => {
              this[key] = value;
            });
          }
          this.updated_at = model.updated_at || timestamp;
          this.synced_at = model.updated_at || timestamp;
          this.timestamps = false;
          await this.save();
          this.timestamps = true;
          return this;
        })(r))
        .catch((error) => {
          // console.warn(this.constructor.name, error);
          // if (error.response && error.response.status === 404) {
          //   return this.forceDelete().then(() => this);
          // }
          // if (error.response && error.response.status === 422) {
          //   return this.sync();
          // }
          let { message } = error;
          if (error.response) {
            switch (error.response.status) {
              case 403:
                message = (error.response.data || {}).message || error.message;
                break;
              case 422:
                message = Object.keys(error.response.data.errors).map((k) => (
                  `${error.response.data.errors[k].join(', ')}`
                )).join(', ');
                break;
              default:
                message = error.response.message || error.message;
                break;
            }
          }
          if (error && !error.response && error instanceof Error && !error.message.includes('Request Timeout') && !error.message.includes('Network Error')) {
            Sentry.captureException(error);
          }
          if (message === 'Network Error') message = translate(message);
          throw new Error(`[${this.constructor.name}]:\n${message}`);
        });
    }
}
